import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import { Alert, Box, IconButton, InputBase, Stack, Tooltip, Typography } from '@mui/material';
import { Routes, ZoomLevels } from 'enums';
import ConfirmDeleteLeadListDialog from 'features/lead-list-research/lead-list-storage/confirm-delete-lead-list-dialog';
import LeadListTable from 'features/lead-list-research/lead-list-storage/lead-list-table';
import PaiPagination from 'features/ui/base-components/pai-pagination';
import PaiSpinningLoader from 'features/ui/base-components/pai-spinning-loader';
import PaiButton from 'features/ui/base-components/pai-button';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { makeStyles } from 'tss-react/mui';
import { LeadListFilterParams, useDeleteLeadList, useGetLeadLists } from '../services/lead-list-research.service';
import useAppBarNavigationStore, { NavigationStates } from '../stores/app-bar-navigation-store';

const LeadListStorage: React.FC = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setActiveTab = useAppBarNavigationStore((state) => state.setActiveTab);
  const [filters, setFilters] = useState<LeadListFilterParams>({
    page: 1,
    limit: 25,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedListId, setSelectedListId] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useGetLeadLists(filters);
  const { mutate: deleteLeadList, isPending: isDeleting } = useDeleteLeadList();

  // Set the active tab when component mounts
  useEffect(() => {
    setActiveTab(NavigationStates.LEAD_LIST_RESEARCH);
  }, [setActiveTab]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Update filters with search term
    setFilters((prev) => ({
      ...prev,
      product: searchTerm,
      page: 1, // Reset to first page on new search
    }));
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setFilters((prev) => ({
      ...prev,
      product: '',
      page: 1,
    }));
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setFilters((prev) => ({
      ...prev,
      page,
    }));
  };

  const handleViewList = (id: string) => {
    navigate(`${Routes.LEAD_LIST_STORAGE}/${id}`);
  };

  const handleDeleteClick = (id: string) => {
    setSelectedListId(id);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedListId) {
      deleteLeadList(selectedListId, {
        onSuccess: () => {
          setDeleteConfirmOpen(false);
          refetch();
        },
        onError: (error) => {
          console.error('Error deleting lead list:', error);
          alert('Failed to delete lead list. Please try again.');
        },
      });
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setSelectedListId(null);
  };

  const handleRefresh = () => {
    refetch();
  };

  if (error) {
    return <Alert severity={'error'}>Error loading lead lists: {error instanceof Error ? error.message : 'Unknown error'}</Alert>;
  }

  const listCount = data?.pagination?.total ?? data?.data?.length ?? 0;
  const leadCount = (data?.data || []).reduce((sum: number, list: any) => sum + Number(list?.metadata?.leadCount ?? 0), 0);
  const isSinglePage = !data?.pagination || data.pagination.pages <= 1;
  const subtitle = t(isSinglePage ? 'leadListSummaryTotal' : 'leadListSummaryPage', {
    lists: listCount.toLocaleString(),
    leads: leadCount.toLocaleString(),
  });

  return (
    <div className={classes.container}>
      <Box className={classes.header}>
        <Stack direction={'column'} gap={'6px'}>
          <Typography className={classes.title}>{t('myLeadLists')}</Typography>
          <Typography className={classes.subtitle}>{subtitle}</Typography>
        </Stack>

        <Stack direction={'row'} gap={'12px'} alignItems={'center'}>
          <form onSubmit={handleSearch} className={classes.searchForm}>
            <div className={classes.searchField}>
              <SearchIcon className={classes.searchIcon} />
              <InputBase
                className={classes.searchInput}
                placeholder={t('searchLeadListPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <IconButton className={classes.clearButton} size={'small'} onClick={handleClearSearch}>
                  <CloseIcon />
                </IconButton>
              )}
            </div>
          </form>
          <Tooltip title={t('refresh')}>
            <IconButton className={classes.refreshButton} onClick={handleRefresh}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <PaiButton
            text={t('newLeadList')}
            border={'rounded'}
            noLineBreak={true}
            action={() => navigate('/lead-list-research')}
            style={{ height: '40px', borderRadius: '8px', padding: '0 22px', fontSize: '15px', fontWeight: 700 }}
          />
        </Stack>
      </Box>

      <Box className={classes.content}>
        {isLoading ? (
          <Box display={'flex'} justifyContent={'center'} alignItems={'center'} minHeight={'200px'}>
            <PaiSpinningLoader size={'medium'} />
          </Box>
        ) : (
          <LeadListTable leadLists={data?.data || []} handleViewList={handleViewList} handleDeleteClick={handleDeleteClick} />
        )}

        {data?.pagination && data.pagination.pages > 1 && (
          <Stack direction={'row'} justifyContent={'center'} className={classes.paginationBar}>
            <PaiPagination totalPages={data.pagination.pages} page={filters.page || 1} handleChangePage={handlePageChange} />
          </Stack>
        )}
      </Box>

      <ConfirmDeleteLeadListDialog
        open={deleteConfirmOpen}
        loading={isDeleting}
        handleClose={handleCancelDelete}
        handleConfirmDelete={handleConfirmDelete}
      />
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    background: theme.palette.backgrounds.main_new,
    height: 'calc(100vh - 60px - 32px)',
    borderRadius: '6px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',

    [theme.breakpoints.down('xl')]: {
      height: `calc((100vh / ${ZoomLevels.XL}) - 60px - 32px)`,
    },
    [theme.breakpoints.down(1300)]: {
      height: `calc((100vh / ${ZoomLevels.CUSTOM_LG}) - 60px - 32px)`,
    },
    [theme.breakpoints.down('lg')]: {
      height: `calc((100vh / ${ZoomLevels.LG}) - 60px - 32px)`,
    },
    [theme.breakpoints.down(1000)]: {
      height: `calc((100vh / ${ZoomLevels.CUSTOM_MD}) - 60px - 32px)`,
    },
    [theme.breakpoints.down('md')]: {
      height: `calc((100vh / ${ZoomLevels.MD}) - 60px - 32px)`,
    },
  },
  header: {
    padding: '28px 24px 20px 24px',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: '24px',
    flexWrap: 'wrap',
  },
  title: {
    fontSize: theme.typography.pxToRem(26),
    fontWeight: 500,
    letterSpacing: '-0.2px',
    lineHeight: 1.2,
  },
  subtitle: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.greys.medium,
  },
  searchForm: {
    display: 'flex',
  },
  searchField: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    width: '320px',
    height: '40px',
    border: `1px solid ${theme.palette.greys.lighter}`,
    background: theme.palette.common.white,
    borderRadius: '8px',
    padding: '0 6px 0 14px',
    '&:focus-within': {
      borderColor: theme.palette.primary.main,
    },
  },
  searchIcon: {
    fontSize: theme.typography.pxToRem(18),
    color: theme.palette.greys.medium,
    flexShrink: 0,
  },
  searchInput: {
    flex: 1,
    minWidth: 0,
    marginLeft: '10px',
    fontSize: theme.typography.pxToRem(15),
  },
  clearButton: {
    padding: '4px',
    color: theme.palette.greys.medium,
    '& .MuiSvgIcon-root': {
      fontSize: theme.typography.pxToRem(16),
    },
  },
  refreshButton: {
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    color: theme.palette.greys.medium,
    '&:hover': {
      backgroundColor: theme.palette.common.white,
      color: theme.palette.primary.main,
    },
  },
  content: {
    padding: '0 24px 24px 24px',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  paginationBar: {
    paddingTop: '16px',
    flexShrink: 0,
  },
}));

export default LeadListStorage;
